import { useState, useEffect } from 'react';
import { newsletterService } from '../services/apiService';

const NewsletterForm = ({ newsletter, type, onClose, onSave }) => {
  const MAX_DESCRIPTION_LENGTH = 500;
  
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    type: type || '',
    publishDate: new Date().toISOString().split('T')[0]
  });
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [coverImage, setCoverImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (newsletter) {
      setFormData({
        title: newsletter.title,
        url: newsletter.url,
        description: newsletter.description,
        type: newsletter.type,
        publishDate: new Date(newsletter.publishDate).toISOString().split('T')[0]
      });
      setWordCount(newsletter.description.trim() ? newsletter.description.trim().split(/\s+/).length : 0);
      setCharCount(newsletter.description.length);
    } else if (type) {
      setFormData(prev => ({ ...prev, type }));
    }
  }, [newsletter, type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'description') {
      setWordCount(value.trim() ? value.trim().split(/\s+/).length : 0);
      setCharCount(value.length);
    }
  };

  const handleCoverImageChange = (e) => {
    setCoverImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate description length before submission
    if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
      setError(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('url', formData.url);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('publishDate', formData.publishDate);
      
      if (coverImage) {
        formDataToSend.append('coverImage', coverImage);
      }

      if (newsletter) {
        await newsletterService.updateNewsletter(newsletter._id, formDataToSend);
      } else {
        await newsletterService.createNewsletter(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="text-red-500">{error}</div>}
      
      {newsletter && newsletter.coverImage && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Current Cover Image</label>
          <img 
            src={`data:image/jpeg;base64,${newsletter.coverImage}`} 
            alt="Current cover" 
            className="mt-2 h-48 w-full object-cover rounded"
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Cover Image *</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleCoverImageChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#0e3d1f]"
          required={!newsletter}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Newsletter title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">URL *</label>
        <input
          type="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="https://example.com/newsletter"
        />
      </div>

      {!type && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Type *</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
            required
          >
            <option value="">Select a type</option>
            <option value="simply-smart">Simply Smart</option>
            <option value="globally-smart">Globally Smart</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">Publish Date *</label>
        <input
          type="date"
          name="publishDate"
          value={formData.publishDate}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <span className={`text-xs ${charCount > MAX_DESCRIPTION_LENGTH ? 'text-red-600' : 'text-gray-500'}`}>
            {wordCount} words, {charCount}/{MAX_DESCRIPTION_LENGTH} chars
          </span>
        </div>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          maxLength={MAX_DESCRIPTION_LENGTH}
          className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534] ${
            charCount > MAX_DESCRIPTION_LENGTH ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Brief description of the newsletter"
        ></textarea>
        {charCount > MAX_DESCRIPTION_LENGTH && (
          <p className="mt-1 text-sm text-red-600">
            Description exceeds maximum length by {charCount - MAX_DESCRIPTION_LENGTH} characters
          </p>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || charCount > MAX_DESCRIPTION_LENGTH}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534] disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : newsletter ? 'Update Newsletter' : 'Create Newsletter'}
        </button>
      </div>
    </form>
  );
};

export default NewsletterForm;